import {
  FC,
  useEffect,
  useState,
  useCallback,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
  useRef
} from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import Dropdown from '@/components/Dropdown/Dropdown'

import { SearchBoxProps } from '../SearchNearParking.types'

import { useSearchBox } from './useSearchBox'

export interface SearchBoxRefHandle {
  getFirstOption: () => any | null
  selectFirstOption: () => boolean
  findBestMatch: (query: string) => boolean
  triggerSearch: () => void
}

const SearchBox = forwardRef<SearchBoxRefHandle, SearchBoxProps>(
  ({ market, placeholder }, ref) => {
    const { control } = useFormContext()
    const { field } = useController({
      name: 'location',
      control
    })
    const { t } = useTranslation('parkings')
    const router = useRouter()
    const formRef = useRef<HTMLFormElement | null>(null)
    const searchButtonRef = useRef<HTMLButtonElement | null>(null)
    const {
      setInputValue,
      noOptionsMessage,
      inputValue,
      options,
      getFirstOption,
      saveToSearchHistory
    } = useSearchBox({ market, t, skipMapboxSuggestions: false })
    const [isSetted, setIsSetted] = useState(false)
    const isInternalChange = useRef(false)
    const isSelectingOption = useRef(false)

    useEffect(() => {
      const findFormAndButton = () => {
        let element = document.querySelector('[data-test-id="dropdown"]')
        while (element && element.tagName !== 'FORM') {
          element = element.parentElement
        }
        formRef.current = element as HTMLFormElement

        if (formRef.current) {
          const button = formRef.current.querySelector(
            '[data-test-id="search-button-wrapper"]'
          )
          searchButtonRef.current = button as HTMLButtonElement
        }
      }

      findFormAndButton()
      setTimeout(findFormAndButton, 500)
    }, [])

    const triggerSearch = useCallback(() => {
      if (searchButtonRef.current) {
        const event = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
        searchButtonRef.current.dispatchEvent(event)
      } else if (formRef.current) {
        formRef.current.dispatchEvent(
          new Event('submit', { bubbles: true, cancelable: true })
        )
      }
    }, [])

    const calculateSimilarity = useCallback(
      (s1: string, s2: string): number => {
        s1 = s1.toLowerCase()
        s2 = s2.toLowerCase()

        if (s1 === s2) return 1

        if (s1.includes(s2) || s2.includes(s1)) {
          const longerLength = Math.max(s1.length, s2.length)
          const shorterLength = Math.min(s1.length, s2.length)
          return 0.8 * (shorterLength / longerLength)
        }

        const words1 = s1.split(/\s+/)
        const words2 = s2.split(/\s+/)

        let matchingWords = 0
        for (const word1 of words1) {
          if (word1.length < 3) continue
          for (const word2 of words2) {
            if (word2.length < 3) continue
            if (word1.includes(word2) || word2.includes(word1)) {
              matchingWords++
              break
            }
          }
        }

        const wordMatchScore =
          words1.length > 0 ? matchingWords / words1.length : 0

        const maxLen = Math.max(s1.length, s2.length)
        if (maxLen === 0) return 1.0

        let sameChars = 0
        for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
          if (s1[i] === s2[i]) sameChars++
        }

        const charSimilarity = sameChars / maxLen

        return 0.7 * wordMatchScore + 0.3 * charSimilarity
      },
      []
    )

    const findBestMatch = useCallback(
      (query: string = inputValue): boolean => {
        if (!query || query.length < 3 || options.length === 0) return false

        let bestMatch = null
        let highestScore = 0

        for (const option of options) {
          if (option.label.toLowerCase() === query.toLowerCase()) {
            isSelectingOption.current = true
            field.onChange(option)
            saveToSearchHistory(option)
            // Set the input value to match the selected option
            setInputValue(option.label)
            return true
          }
        }

        for (const option of options) {
          const score = calculateSimilarity(option.label, query)
          if (score > highestScore) {
            highestScore = score
            bestMatch = option
          }
        }

        if (bestMatch && highestScore > 0.4) {
          isSelectingOption.current = true
          field.onChange(bestMatch)
          saveToSearchHistory(bestMatch)
          // Set the input value to match the selected option
          setInputValue(bestMatch.label)
          return true
        }

        if (options.length > 0) {
          isSelectingOption.current = true
          field.onChange(options[0])
          saveToSearchHistory(options[0])
          // Set the input value to match the selected option
          setInputValue(options[0].label)
          return true
        }

        return false
      },
      [
        options,
        inputValue,
        field,
        calculateSimilarity,
        saveToSearchHistory,
        setInputValue
      ]
    )

    const selectFirstOption = useCallback(() => {
      if (findBestMatch()) return true

      const firstOption = getFirstOption()
      if (firstOption) {
        isSelectingOption.current = true
        field.onChange(firstOption)
        saveToSearchHistory(firstOption)
        // Set the input value to match the selected option
        setInputValue(firstOption.label)
        return true
      }
      return false
    }, [
      findBestMatch,
      getFirstOption,
      field,
      saveToSearchHistory,
      setInputValue
    ])

    useImperativeHandle(ref, () => ({
      getFirstOption,
      selectFirstOption,
      findBestMatch,
      triggerSearch
    }))

    useEffect(() => {
      if (!inputValue && field?.value?.label && !isSetted) {
        isInternalChange.current = true
        isSelectingOption.current = true
        setInputValue(field.value.label)
        setIsSetted(true)
        setTimeout(() => {
          isInternalChange.current = false
          isSelectingOption.current = false
        }, 300)
      }
    }, [field, inputValue, isSetted, setInputValue])

    useEffect(() => {
      const locationLabel = router.query.locationLabel as string | undefined

      if (
        locationLabel !== undefined &&
        locationLabel !== inputValue &&
        !isInternalChange.current
      ) {
        isInternalChange.current = true
        isSelectingOption.current = true
        setInputValue(locationLabel)
        setTimeout(() => {
          isInternalChange.current = false
          isSelectingOption.current = false
        }, 300)
      }
    }, [router.query.locationLabel, inputValue, setInputValue])

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault()

          if (inputValue && inputValue.length > 0) {
            if (selectFirstOption()) {
              if (formRef.current) {
                formRef.current.dispatchEvent(
                  new Event('submit', { bubbles: true, cancelable: true })
                )
              }
            }
          }
        }
      },
      [selectFirstOption, inputValue]
    )

    const handleInputChange = useCallback(
      (value: string) => {
        isInternalChange.current = true
        setInputValue(value)
        setTimeout(() => {
          isInternalChange.current = false
        }, 0)
      },
      [setInputValue]
    )

    const handleOptionSelect = useCallback(
      (option: any) => {
        isSelectingOption.current = true
        field.onChange(option)
        saveToSearchHistory(option)
        setInputValue(option.label)
        setTimeout(() => {
          isSelectingOption.current = false
        }, 300)
      },
      [field, saveToSearchHistory, setInputValue]
    )

    return (
      <Dropdown
        options={options}
        noOptionsMessage={inputValue?.length > 3 ? noOptionsMessage : null}
        inputValue={inputValue}
        placeholder={placeholder}
        setInputValue={handleInputChange}
        onSelectOption={handleOptionSelect}
        field={field}
        data-test-id="dropdown"
        onKeyDown={handleKeyDown}
        hideListWhileTyping={true}
      />
    )
  }
)

SearchBox.displayName = 'SearchBox'

export default SearchBox
